"use client";

import { useEffect, useState } from "react";

const STORAGE_CHANGE_EVENT = "usePersistedState:change";

interface StorageChangeDetail {
  key: string;
  value: string | null;
}

function dispatchStorageChange(key: string, value: string | null) {
  window.dispatchEvent(new CustomEvent<StorageChangeDetail>(STORAGE_CHANGE_EVENT, { detail: { key, value } }));
}

function readFromStorage<T extends string>(key: string, defaultValue: T | null): T | null {
  if (typeof window === "undefined") return defaultValue;
  try {
    const stored = localStorage.getItem(key);
    return stored !== null ? (stored as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Persists state to localStorage and syncs changes automatically.
 * T must be a string type (or string enum) since localStorage only stores strings.
 * Listeners on the same key (same or other tabs) will receive updates.
 */
export function usePersistedState<T extends string>(key: string, defaultValue: T | null = null) {
  const [prevKey, setPrevKey] = useState(key);
  const [state, setState] = useState<T | null>(() => readFromStorage(key, defaultValue));

  if (prevKey !== key) {
    setPrevKey(key);
    setState(readFromStorage(key, defaultValue));
  }

  useEffect(() => {
    try {
      if (state === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, state);
      }
      dispatchStorageChange(key, state);
    } catch {
      // Silently ignore storage errors
    }
  }, [state, key]);

  useEffect(() => {
    const handleCustomEvent = (e: Event) => {
      const { key: changedKey, value } = (e as CustomEvent<StorageChangeDetail>).detail;
      if (changedKey === key) {
        setState(value !== null ? (value as T) : null);
      }
    };

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === key) {
        setState(e.newValue !== null ? (e.newValue as T) : null);
      }
    };

    window.addEventListener(STORAGE_CHANGE_EVENT, handleCustomEvent);
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener(STORAGE_CHANGE_EVENT, handleCustomEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [key]);

  return [state, setState] as const;
}
